import { CreditCard, CheckCircle, X } from "lucide-react";

export default function TransactionReviewStep({ 
  step, 
  currentTransaction, 
  loading,
  onFulfill,
  onCancel 
}) {
  if (step < 2 || !currentTransaction) return null;
  
  const getStatusConfig = () => {
    switch (currentTransaction.status) {
      case 'FULFILLED':
        return {
          bgColor: 'bg-green-100',
          icon: <CheckCircle className="h-5 w-5 text-green-600" />,
          description: 'Transaction completed successfully',
          badgeColor: 'bg-green-100 text-green-800',
          badgeText: 'Completed'
        };
      case 'CANCELLED':
        return {
          bgColor: 'bg-red-100',
          icon: <X className="h-5 w-5 text-red-600" />,
          description: 'Transaction was cancelled',
          badgeColor: 'bg-red-100 text-red-800',
          badgeText: 'Cancelled'
        };
      default:
        return {
          bgColor: 'bg-blue-100',
          icon: <CreditCard className="h-5 w-5 text-blue-600" />,
          description: 'Review transaction details and choose action',
          badgeColor: null,
          badgeText: null
        };
    }
  };

  const statusConfig = getStatusConfig();
  const isCompleted = currentTransaction.status === 'FULFILLED' || currentTransaction.status === 'CANCELLED';

  return (
    <div className="theme-card rounded-lg p-4 md:p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className={`p-2 rounded-lg ${statusConfig.bgColor}`}>
          {statusConfig.icon}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Step 2: Transaction Review</h2>
          <p className="text-sm text-gray-600">{statusConfig.description}</p>
        </div>
        {isCompleted && (
          <div className="ml-auto">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.badgeColor}`}>
              {statusConfig.badgeText}
            </span>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div className="space-y-6">
        {/* Basic Transaction Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Transaction Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Reference ID</p>
              <p className="font-mono text-sm text-gray-900 break-all">{currentTransaction.reference_id || currentTransaction.uuid}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                currentTransaction.status === 'FULFILLED' ? 'bg-green-100 text-green-800' :
                currentTransaction.status === 'CANCELLED' ? 'bg-red-100 text-red-800' : 
                'bg-blue-100 text-blue-800'
              }`}>
                {currentTransaction.status_label}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Customer</p>
              <p className="font-medium text-gray-900">{currentTransaction.customer?.name}</p>
              <p className="text-sm text-gray-600">{currentTransaction.customer?.mobile || currentTransaction.mobile}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date & Time</p>
              <p className="text-sm text-gray-900">{new Date(currentTransaction.created_at).toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        {/* Billable Amount - Simple display */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-blue-900">Billable Amount:</span>
            <span className="text-2xl font-bold text-blue-600">
              {currentTransaction.financial_summary?.billable_amount?.formatted || 
               `₹${parseFloat(currentTransaction.billable_amount || 0).toLocaleString('en-IN')}`}
            </span>
          </div>
        </div>

        {/* Business Details */}
        {currentTransaction.business && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Business Details</h3>
            <p className="font-medium text-gray-900">{currentTransaction.business.business_name}</p>
            <p className="text-sm text-gray-600">{currentTransaction.business.address}</p>
          </div>
        )}

        {/* Transaction completed states */}
        {currentTransaction.status === 'FULFILLED' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-800 font-medium">Transaction Completed Successfully</span>
            </div>
            {currentTransaction.fulfilled_at && (
              <p className="text-sm text-green-700 mt-2">
                Completed at: {new Date(currentTransaction.fulfilled_at).toLocaleString('en-IN')}
              </p>
            )}
          </div>
        )}

        {currentTransaction.status === 'CANCELLED' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <X className="h-5 w-5 text-red-600" />
              <span className="text-red-800 font-medium">Transaction was cancelled</span>
            </div>
          </div>
        )}

        {/* Action Buttons - Only show for initiated transactions */}
        {currentTransaction.status === 'INITIATED' && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <button
              onClick={onFulfill}
              disabled={loading}
              className="w-full sm:flex-1 theme-button-gradient py-3 px-6 flex items-center justify-center space-x-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Deal</span>
                </>
              )}
            </button>
            
            <button
              onClick={onCancel}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="h-5 w-5" />
              <span>Cancel</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}